import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { connect, FormikValues } from 'formik'
import { flatten } from 'lodash'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { Button } from '@/components/Button'
import { AddCircle } from '@/components/Icons'
import Modal from '@/components/Modal'
import { FormLabel, Row } from '@/components/StyledComponents'
import messages from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/messages'
import { Variable } from '@/pages/SpaceAppBuilder/utilities/interfaces'

import BooleanSimpleRule from './components/BooleanSimpleRule'
import Delete from './components/DeleteIcon'
import { Rule } from './components/Rule'
import { AddRuleProps, ConditionsParams, ConditionType, DeleteConditionProps } from './interfaces'
import { newCondition } from './utils'

//<editor-fold desc="Styled Components">
const ConditionHeader = styled.div`
  h3 {
    margin: 0.25rem 0.25rem 0.25rem 0;
  }
  align-items: center;
  justify-content: start;
`

const DeleteButton = styled.div`
  margin-left: auto;
  cursor: pointer;
  min-width: 0;
  &:hover {
    g {
      fill: #db3939;
    }
  }
`

const RuleTitle = styled.h3`
  font-size: 1rem;
`

const ConditionDefinition = styled.div`
  font-size: 0.7rem;
  min-width: 87px;
  color: #8a8a8a;
  margin-bottom: 0.5rem;
`

const StyledConditions = styled.div`
  .filter {
    .column {
      min-width: 0;
      margin: auto 0.25rem;
    }
  }

  .field__wrapper,
  .form__field {
    margin: initial;
  }
`

const StyledRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;
`
//</editor-fold>

const Conditions = ({
  name,
  variables = [],
  conditions = [],
  readOnly = false,
  leftComparatorAsTextInput = false,
  leftComparatorPlaceholder,
  formik
}: ConditionsParams & FormikValues) => {
  const intl = useIntl()

  const flattenedVariables = flatten(variables) as Variable[]
  return flattenedVariables?.length > 0 ? (
    <StyledConditions>
      {conditions?.map((condition: ConditionType, conditionIndex: number) => {
        return (
          <div className="mt-4 rounded-md border p-3 shadow-sm" key={conditionIndex}>
            <ConditionHeader className="row pr-2">
              <RuleTitle>
                <FormattedMessage {...messages.ruleTitle} values={{ index: conditionIndex + 1 }} />
              </RuleTitle>
              {conditions.length > 1 && !readOnly && (
                <DeleteCondition
                  onClick={() => {
                    const filteredArray = conditions.filter((_: any, idx: number) => idx !== conditionIndex)
                    formik.setFieldValue(name, filteredArray)
                  }}
                />
              )}
            </ConditionHeader>
            <Rule
              name={`${name}[${conditionIndex}]`}
              condition={condition}
              variables={variables}
              readOnly={readOnly}
              leftComparatorAsTextInput={leftComparatorAsTextInput}
              leftComparatorPlaceholder={leftComparatorPlaceholder}
            />
          </div>
        )
      })}
      {!readOnly && (
        <div className="row">
          <AddRule
            onClick={() => {
              const conditionToAdd = newCondition()
              formik.setFieldValue(name, [...conditions, conditionToAdd])
            }}
          />
        </div>
      )}
      <ConditionDefinition>
        <FormattedMessage {...messages.ruleDefinition} />
      </ConditionDefinition>
      <BooleanSimpleRule conditions={conditions} />
    </StyledConditions>
  ) : (
    <StyledRow>
      <FormLabel>{intl.formatMessage(messages.booleanVarErrorMessage)}</FormLabel>
    </StyledRow>
  )
}

const AddRule = ({ onClick }: AddRuleProps) => {
  return (
    <div className="m-1">
      <Button color="transparent" onClick={onClick}>
        <div className="flex items-center space-x-2 text-blue-600">
          <AddCircle className="mr-1" />
          Add an OR rule
        </div>
      </Button>
    </div>
  )
}

const DeleteCondition = ({ onClick }: DeleteConditionProps) => {
  const [isPromptOpen, setIsPromptOpen] = useState(false)
  return (
    <>
      <Modal size="sm" isOpen={isPromptOpen} onClose={() => setIsPromptOpen(false)}>
        <div className="flex items-center space-x-4">
          <div className="mx-auto flex h-12 w-16 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <FormattedMessage {...messages.confirmDeleteCondition} />
        </div>
        <div className="mt-12 flex items-center justify-end gap-x-6">
          <Button
            color="transparent"
            onClick={() => {
              setIsPromptOpen(false)
            }}
          >
            <FormattedMessage id="Cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            color="warning"
            onClick={() => {
              setIsPromptOpen(false)
              onClick()
            }}
          >
            <FormattedMessage id="Delete" defaultMessage="Delete" />
          </Button>
        </div>
      </Modal>
      <DeleteButton onClick={() => setIsPromptOpen(true)}>
        <Delete />
      </DeleteButton>
    </>
  )
}

export default connect(Conditions)
